import { Component, EventEmitter, inject, Output } from '@angular/core';
import { AssemblyComponent } from '../assembly/assembly.component';
import {
  ColumnDate,
  ShownPeriodsData,
  FsExcelExportType,
  TOTAL_CHECK_FAILED_TEXT,
} from '../../utils/common/utilityClasses';
import { KeyValue } from '@angular/common';
import { BsExcelExportData } from '../../../../services/balance-sheet/bsTemplate';
import { BsService } from '../../services/bs/bs.service';
import { AssemblyService } from '../../services/assembly/assembly.service';
import { BsPeriodsSelectors } from '../../utils/bs-pl/utils';
import { FsSelectorPeriod } from '../../utils/common/FsSelectorPeriod';
import { FsSums } from '../../utils/common/FsSums';
import { FsCalculationsService } from '../../services/common/fs-calculations.service';

@Component({
  selector: 'app-bs-page',
  templateUrl: './bs-page.component.html',
  styleUrls: ['../assembly/assembly.component.scss'],
  providers: [BsService, AssemblyService],
})
export class BsPageComponent extends AssemblyComponent {
  @Output() bsDateColumnsChangeEmitter = new EventEmitter<Map<string, ColumnDate>>();
  periodsSelectors: BsPeriodsSelectors;
  periodsData: ShownPeriodsData;
  selectedBSDateColumns: Map<ColumnOrder, ColumnDate>;

  /**
   * Instance of BsService obtained directly through Angular inject function.
   *
   * This approach streamlines dependency injection and prevents constructor overcrowding
   * in scenarios where a class inherits from a parent with multiple dependencies.
   */
  readonly bsService: BsService = inject(BsService);

  protected readonly TOTAL_CHECK_FAILED_TEXT = TOTAL_CHECK_FAILED_TEXT;

  protected override onInitSubscribe(financialStatementPeriod: FsSelectorPeriod): void {
    super.onInitSubscribe(financialStatementPeriod);
    this.initBS();
  }

  protected override onChange(): void {
    if (!this.financialStatement) {
      return;
    }
    this.parts = this.financialStatement.balanceSheetParts;
    this.initBS();
    this.checkEmptyState();
  }

  override calculateTotalsAndSums(): void {
    if (!this.financialStatement) {
      return;
    }
    const selectedDates = this.bsService.getSelectedDates(this.selectedBSDateColumns);
    FsCalculationsService.calculateBalanceSheetTotals(this.financialStatement.balanceSheetNetTotals, this.parts, false, selectedDates);
  }

  calculateDateColumnsDifferences(column: KeyValue<ColumnOrder, ColumnDate>, sums: FsSums): number {
    if (!this.selectedBSDateColumns.get(column.key).selectedDate) {
      return 0;
    }
    switch (column.key) {
      case ColumnOrder.FIRST:
        const secondColumnDate = this.selectedBSDateColumns.get(ColumnOrder.SECOND).selectedDate;
        return secondColumnDate ? sums.dateColumnSums.get(secondColumnDate) - sums.dateColumnSums.get(column.value.selectedDate) : 0;

      case ColumnOrder.SECOND:
        const thirdColumnDate = this.selectedBSDateColumns.get(ColumnOrder.THIRD).selectedDate;
        return thirdColumnDate ? sums.dateColumnSums.get(thirdColumnDate)
          - sums.dateColumnSums.get(column.value.selectedDate) : 0;

      case ColumnOrder.THIRD:
        return column.value.selectedDate ? sums.balance - sums.dateColumnSums.get(column.value.selectedDate) : 0;

      default:
        return null;
    }
  }

  changeBSDateColumn(columnOrder: ColumnOrder, selectedDate: string): void {
    this.bsService.changeBSDateColumn(columnOrder, selectedDate, this.selectedBSDateColumns);
    this.calculateTotalsAndSums();
    this.bsDateColumnsChangeEmitter.emit(this.selectedBSDateColumns);
  }

  /**
   * Handles the change in the number of shown BS periods,
   * clearing dateColumns selectedDate from left to right based on the selected number of shown periods.
   *
   * @param shownBSPeriodsNumber - The number of shown BS periods.
   */
  updateNumberComparedBSPeriods(shownBSPeriodsNumber: number): void {
    this.periodsSelectors.numberSelector.selected = shownBSPeriodsNumber;

    switch (shownBSPeriodsNumber) {
      case 1:
        this.changeBSDateColumn(ColumnOrder.FIRST, null);
        this.changeBSDateColumn(ColumnOrder.SECOND, null);
        this.changeBSDateColumn(ColumnOrder.THIRD, null);
        break;
      case 2:
        this.setDefaultValuesForBSDateColumns(this.periodsSelectors.typeSelector.selected);
        this.changeBSDateColumn(ColumnOrder.FIRST, null);
        this.changeBSDateColumn(ColumnOrder.SECOND, null);
        break;
      case 3:
        this.setDefaultValuesForBSDateColumns(this.periodsSelectors.typeSelector.selected);
        this.changeBSDateColumn(ColumnOrder.FIRST, null);
        break;
      case 4:
        this.setDefaultValuesForBSDateColumns(this.periodsSelectors.typeSelector.selected);
        break;
    }
  }

  handleShownBSPeriodTypeChange(selectedBSPeriodType: typeof BS_PERIOD_TYPES[number]): void {
    this.periodsSelectors.typeSelector.selected = selectedBSPeriodType;
    this.periodsSelectors.numberSelector.data = this.bsService.calculateBSPeriodsData(selectedBSPeriodType, this.periodsData);
    // check if the previous number of shown BS periods is not in the new available options
    if (!this.periodsSelectors.numberSelector.data.includes(this.periodsSelectors.numberSelector.selected)) {
      this.periodsSelectors.numberSelector.selected = this.periodsSelectors.numberSelector.data[0];
    }
    this.updateNumberComparedBSPeriods(this.periodsSelectors.numberSelector.selected);
  }

  exportExcelBs(): void {
    this.fsExcelService.exportExcel(FsExcelExportType.BS, this.exportBalanceSheetExcelData());
  }

  private initBS(): void {
    if (!this.financialStatement) {
      return;
    }
    const bsData: BsPageData = this.bsService.initBsPageData(this.financialStatement);
    this.periodsData = bsData.periodsData;
    this.periodsSelectors = bsData.periodsSelectors;
    this.selectedBSDateColumns = bsData.selectedBSDateColumns;

    this.bsDateColumnsChangeEmitter.emit(this.selectedBSDateColumns);
  }

  private setDefaultValuesForBSDateColumns(selectedBSPeriodType: string): void {
    this.bsService.setDefaultValuesForBSDateColumns(selectedBSPeriodType, this.selectedBSDateColumns,
      this.financialStatementPeriod, this.periodsData, this.financialStatement.gl);
    this.calculateTotalsAndSums();
    this.bsDateColumnsChangeEmitter.emit(this.selectedBSDateColumns);
  }

  private exportBalanceSheetExcelData(): BsExcelExportData {
    return {
      selectedBasedDataColumns: this.selectedBSDateColumns,
      parts: this.financialStatement.balanceSheetParts,
      selectedPeriod: this.financialStatement.period,
    };
  }

  private checkEmptyState(): void {
    this.show.emptyState = !this.financialStatement || !this.financialStatement.balanceSheetParts.some((part) =>
      part.financialStatementItems.some((item) =>
        item.financialStatementAccounts.length > 0));
  }
}

export const BS_PERIOD_TYPES = ['Quarters', 'Years'] as const;
export const MAX_DATE_COLUMNS_AMOUNT = 4;

export enum ColumnOrder {
  FIRST = 'firstColumn',
  SECOND = 'secondColumn',
  THIRD = 'thirdColumn',
}

export class BsPageData {
  periodsData: ShownPeriodsData;
  periodsSelectors: BsPeriodsSelectors;
  selectedBSDateColumns: Map<ColumnOrder, ColumnDate>;
}
